import { AdProviderTypes, AdProvidersSettings } from '@/types'
import type { StartRumEvent } from '@/types/logger.types'

import type { AdProvider } from './ad-provider'
import type { FailedAdProvider } from './ad-service'
import { AdProviderStatus } from './types'

function createFailedProvider(type: AdProviderTypes, errorMessage: string): FailedAdProvider {
    return { failed: true, type, errorMessage }
}

type ProviderInitConfig = {
    targeting?: Record<string, string | string[]>
    startRumEvent: StartRumEvent
    auid: string
}

type SetProviderCallback = (index: number, provider: AdProvider | FailedAdProvider) => void

function moduleLoadError(type: AdProviderTypes, cause: unknown): Error {
    const reason = cause instanceof Error ? cause.message : String(cause)
    return new Error(`[${type}] module load failed: ${reason}`)
}

/**
 * Creates provider instance from settings.
 */
export async function createProvider(settings: AdProvidersSettings, config: ProviderInitConfig): Promise<AdProvider> {
    const targeting = { ...config.targeting, ...settings?.options?.targeting }
    const { type } = settings
    switch (type) {
        case AdProviderTypes.GAME_DISTRIBUTION: {
            const { GameDistributionConnector } = await import('./providers/gd-connector/gd-connector').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GameDistributionConnector()
        }
        // TODO: remove when implement provider for tests
        case AdProviderTypes.EMPTY_PROVIDER: {
            const { EmptyAdsProvider } = await import('./providers/empty-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new EmptyAdsProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GOOGLE_BANNER: {
            const { GoogleBannerProvider } = await import('./providers/google-banner').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GoogleBannerProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GOOGLE_AD_SENSE: {
            const { GoogleAdSenseAdProvider } = await import('./providers/google-ad-sense-ad-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GoogleAdSenseAdProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GOOGLE_INSTREAM: {
            const { GoogleInstreamAdsService } = await import('./providers/google-instream').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GoogleInstreamAdsService({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.GOOGLE_AD: {
            const { GoogleAdProvider } = await import('./providers/google-ad').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new GoogleAdProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.DIRECT: {
            const { DirectAdsProvider } = await import('./providers/direct-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new DirectAdsProvider({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.APP: {
            const { AppAdsService } = await import('./providers/app-provider').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new AppAdsService({ ...config, ...settings.options, targeting })
        }
        case AdProviderTypes.HEADER_BIDDING_AD: {
            const { HeaderBiddingAdProvider } = await import('./providers/header-bidding-ad').catch((e) => {
                throw moduleLoadError(type, e)
            })
            return new HeaderBiddingAdProvider({ ...config, ...settings.options, targeting })
        }
        default:
            throw new Error(`Unsupported ad provider type: "${type}"`)
    }
}

/**
 * Initializes a single provider from settings and calls the setProvider callback when ready.
 */
async function initSingleProvider(
    index: number,
    settings: AdProvidersSettings,
    config: ProviderInitConfig,
    setProvider: SetProviderCallback,
): Promise<void> {
    const providerType = settings.type
    const providerOrder = `${index + 1}`
    const sendRumEvent = config.startRumEvent({
        operationName: 'init-ad-provider',
    })

    let provider: AdProvider | undefined
    try {
        provider = await createProvider(settings, config)
        const result = await provider.serviceStatus

        if (result === AdProviderStatus.online) {
            setProvider(index, provider)
            sendRumEvent({
                status: 'success',
                dimension1: providerType,
                dimension2: providerOrder,
            })
        } else {
            setProvider(index, createFailedProvider(provider.type, provider.initErrorMessage ?? 'Provider is offline'))
            sendRumEvent({
                status: 'error',
                dimension1: providerType,
                dimension2: providerOrder,
                dimension3: provider.initErrorMessage ?? 'Provider is offline',
            })
        }
    } catch (error) {
        const errorMessage =
            provider?.initErrorMessage ??
            (error instanceof Error ? error.message : `Failed to initialize provider: ${JSON.stringify(error)}`)
        setProvider(index, createFailedProvider(providerType, errorMessage))
        sendRumEvent({
            status: 'error',
            dimension1: providerType,
            dimension2: providerOrder,
            dimension3: provider?.initErrorMessage ?? errorMessage,
        })
    }
}

export function resolveProviderNames(settings: AdProvidersSettings[]): string[] {
    const counts = new Map<AdProviderTypes, number>()
    settings.forEach(({ type }) => counts.set(type, (counts.get(type) ?? 0) + 1))
    const seen = new Map<AdProviderTypes, number>()
    return settings.map(({ type }) => {
        const i = (seen.get(type) ?? 0) + 1
        seen.set(type, i)
        return (counts.get(type) ?? 1) > 1 ? `${type}-${i}` : type
    })
}

export function initProviders(
    settings: AdProvidersSettings[],
    config: ProviderInitConfig,
    setProvider: SetProviderCallback,
): void {
    const names = resolveProviderNames(settings)
    settings.forEach((s, index) => {
        initSingleProvider(
            index,
            { ...s, options: { ...s.options, name: names[index] } } as AdProvidersSettings,
            config,
            setProvider,
        )
    })
}

export async function preloadProviders(settings: AdProvidersSettings[]) {
    const preloads = [...new Set(settings.map((s) => s.type))].map((type) => {
        switch (type) {
            case AdProviderTypes.GOOGLE_AD:
            case AdProviderTypes.GOOGLE_BANNER:
                return import('./providers/google-ad-provider-base').then(({ GoogleAdProviderBase }) =>
                    GoogleAdProviderBase.preload(),
                )
            case AdProviderTypes.GOOGLE_INSTREAM:
                return import('./providers/google-instream').then(({ GoogleInstreamAdsService }) =>
                    GoogleInstreamAdsService.preload(),
                )
            case AdProviderTypes.HEADER_BIDDING_AD:
                return import('./providers/header-bidding-ad').then(({ HeaderBiddingAdProvider }) =>
                    HeaderBiddingAdProvider.preload(),
                )
            default:
                return Promise.resolve()
        }
    })

    await Promise.allSettled(preloads)
}
